import type { EquipmentTypeResponseItem } from '../types/EquipmentTypes'

export const equipmentTypeToConstant = (
  equipmentType: EquipmentTypeResponseItem
): string => {
  return equipmentType.name
}

export const equipmentTypesToConstants = (
  equipmentType: EquipmentTypeResponseItem[]
): string[] => {
  return equipmentType.map((eq) => equipmentTypeToConstant(eq))
}

export const equipmentTypeToDisplay = (
  equipmentType: EquipmentTypeResponseItem
): string => {
  return equipmentType.val
}

export const equipmentTypesToDisplay = (
  equipmentTypes: EquipmentTypeResponseItem[]
): string[] => {
  return equipmentTypes.map((eq) => equipmentTypeToDisplay(eq))
}

export const cleanEquipmentTypeString = (str: string): string => {
  // Remove spaces, dashes, and underscores and lowercase
  if (!str) {
    return ''
  }
  return str.toLowerCase().replace(/[\s_-]/g, '')
}

/**
 * Converts a string to an equipment type. Matches on name e.g. "DRY_VAN" or val e.g. "Dry Van".
 *
 * @param displayString - The string to convert
 * @param equipmentTypes - The equipment types to search through
 * @returns The equipment type
 */
export const stringToEquipmentType = (
  displayString: string | undefined,
  equipmentTypes: EquipmentTypeResponseItem[]
): EquipmentTypeResponseItem | undefined => {
  if (!displayString) {
    return undefined
  }
  return equipmentTypes?.find(
    (eq) =>
      cleanEquipmentTypeString(eq.name)
        === cleanEquipmentTypeString(displayString)
      || cleanEquipmentTypeString(eq.val)
        === cleanEquipmentTypeString(displayString)
  )
}

export const stringsToEquipmentTypes = (
  constants: string[],
  equipmentTypes: EquipmentTypeResponseItem[]
): EquipmentTypeResponseItem[] => {
  return (
    constants
      .map((constant) => {
        return stringToEquipmentType(constant, equipmentTypes)
      })
      .filter((eq) => eq !== undefined) || []
  )
}

export const deduplicateEquipmentTypes = (
  equipmentTypes: (string | EquipmentTypeResponseItem)[]
): EquipmentTypeResponseItem[] => {
  if (!Array.isArray(equipmentTypes)) {
    return []
  }

  const uniqueTypes = new Set(
    equipmentTypes
      .filter(Boolean)
      .map((type) => (typeof type === 'string' ? type : type.name))
  )

  return Array.from(uniqueTypes)
    .map((name) => {
      const found = equipmentTypes.find(
        (type) => (typeof type === 'string' ? type : type.name) === name
      )
      return typeof found === 'string'
        ? { name: found, val: found }
        : (found as EquipmentTypeResponseItem)
    })
    .filter(Boolean) as EquipmentTypeResponseItem[]
}

/**
 * Interface for equipment type with count
 */
export interface EquipmentTypeWithCount {
  equipment_type: string
  count: number
}

/**
 * Interface for form values that contain equipment type counts
 */
export interface EquipmentFormValues {
  [key: string]: string
}

export const getEquipmentTypePickerOptions = (
  equipmentTypesMap: Array<EquipmentTypeResponseItem>
): Array<{ label: string; value: string }> => {
  return equipmentTypesMap?.map((type) => ({
    label: type.val,
    value: type.name
  }))
}

/**
 * Converts form data to an array of equipment types with counts
 *
 * @param data - object with equipment type names as keys and counts as values
 * @param equipmentTypesMap - Available equipment type options
 * @returns Array of equipment types with counts, filtered to only include types with count > 0
 */
export const getEquipmentTypesFromObject = (
  data: Record<string, number | string | undefined>,
  equipmentTypesMap: Array<EquipmentTypeResponseItem> | undefined
): EquipmentTypeWithCount[] => {
  if (!equipmentTypesMap) return []

  return Object.entries(data).reduce((acc, [key, value]) => {
    if (!value) {
      return acc
    }
    const equipmentTypeResponseItem = stringToEquipmentType(
      key,
      equipmentTypesMap
    )
    const count = Number.parseInt(value as string, 10)
    if (equipmentTypeResponseItem && count > 0) {
      acc.push({
        equipment_type: equipmentTypeResponseItem.name,
        count: Number.parseInt(value as string, 10)
      })
    }
    return acc
  }, [] as EquipmentTypeWithCount[])
}

/**
 * Converts equipment type counts to an array of equipment types
 *
 * @param equipmentTypeCounts - Equipment type counts
 * @param equipmentTypesMap - Available equipment type options
 * @returns Array of equipment types with counts
 */
export const equipmentTypeCountsToArray = (
  equipmentTypeCounts: EquipmentTypeWithCount[],
  equipmentTypesMap: Array<EquipmentTypeResponseItem>
): Record<string, number> => {
  // convert { equipment_type: string, count: number } to { equipment_type_1_name: string, equipment_type_2_name: string, ... }
  return equipmentTypeCounts.reduce(
    (acc, count) => {
      const equipmentTypeResponseItem = stringToEquipmentType(
        count.equipment_type,
        equipmentTypesMap
      )
      if (equipmentTypeResponseItem) {
        acc[equipmentTypeResponseItem.name] = count.count
      }
      return acc
    },
    {} as Record<string, number>
  )
}
